<script setup>
import { onMounted, onUnmounted, ref, inject, computed } from "vue";
import { router, usePage, Link } from "@inertiajs/vue3";
import Logo from "@/Components/AdminLayout/logo/HomeLogo.vue";
import { useDisplay } from "vuetify";
import LanguageSelectorVue from "@/Components/LayoutItems/LanguageSelector.vue";
import ThemeToggleBtn from "@/Components/ThemeToggleBtn.vue";
import { useThemeSetting } from "@/Composable/ThemeSetting";
import Search from "@/Components/LayoutItems/SearchWeb.vue";
import MenuNav from "@/Components/WebLayout/MenuNav.vue";
import MobileSilderNav from "@/Components/WebLayout/MobileSilderNav.vue";
import useNav from "@/Composable/useNav";
import { isValidHttpUrl } from "@/Composable/utils";
import Login from "@/Pages/Auth/Login.vue";
import Register from "@/Pages/Auth/Register.vue";
import CurrencySelector from "@/Components/CurrencySelector.vue";

const { smAndDown } = useDisplay();
const { isDark } = useThemeSetting();
const { isNavLinkActive } = useNav();
const __t = inject("__t");
const page = usePage();
const userMenu = page.props.userMenu;
const loginDialog = ref(false);
const registerDialog = ref(false);
const drawer = ref(false);
const isScrolled = ref(false);

// Check if we're on home page
const isHomePage = computed(() => {
    return page.component === 'Website/Home';
});

// Handle scroll to detect when at top of page
const handleScroll = () => {
    if (!import.meta.env.SSR) {
        isScrolled.value = window.scrollY > 50;
    }
};

// Setup scroll listener
if (!import.meta.env.SSR) {
    onMounted(() => {
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
    });
}

const closeDialog = () => {
    loginDialog.value = false;
    registerDialog.value = false;
};

if (!import.meta.env.SSR) {
    router.on("finish", () => {
        drawer.value = false;
    });
}

const scrollTop = () => {
    if (!import.meta.env.SSR) {
        window.scrollTo(0, 0);
    }
};

const logout = () => {
    router.post(
        route("logout"),
        {},
        {
            onSuccess: (e) => {
                return window.location.reload();
            },
        }
    );
};
</script>

<template>
    <header :class="[
        'fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-all duration-500',
        !isScrolled
            ? 'bg-white dark:bg-black/40 backdrop-blur-md '
            : 'bg-white dark:bg-[#1e1e1e]/80 shadow-sm border-b border-primary/10 dark:border-white/10'
    ]">
        <div class="container mx-auto px-4 h-20 flex items-center justify-between"
            :class="!isScrolled ? 'dark:text-white' : ''">
            <!-- Logo -->
            <Link href="/" class="flex items-center gap-2">
                <Logo class="h-18 w-auto" height="70" max-width="180px" @click="scrollTop" />
            </Link>

            <!-- Desktop Navigation -->
            <div class="hidden lg:flex items-center gap-8">
                <MenuNav />
            </div>

            <!-- Actions -->
            <div class="hidden lg:flex items-center gap-4">
                <div class="flex items-center gap-2">
                    <!-- <CurrencySelector /> -->
                    <LanguageSelectorVue />
                    <ThemeToggleBtn />
                </div>

                <!-- Auth Check -->
                <div v-if="$page.props.auth?.user" class="flex items-center gap-3">
                    <v-menu rounded>
                        <template v-slot:activator="{ props }">
                            <button v-bind="props" class="relative">
                                <img class="object-cover rounded-full h-10 w-10 border-2 border-primary/20"
                                    :src="$page.props.auth?.user.profile_photo_url"
                                    :alt="$page.props.auth?.user.name" />
                            </button>
                        </template>
                        <v-card class="p-5 mt-3 w-72">
                            <div class="mx-auto text-center">
                                <div class="flex gap-4">
                                    <div class="flex items-center justify-center">
                                        <img class="rounded-full h-12 w-12"
                                            :src="$page.props.auth?.user.profile_photo_url"
                                            :alt="$page.props.auth?.user.name" />
                                    </div>
                                    <div class="mt-2 text-start">
                                        <h3 class="font-bold">
                                            {{ $page.props.auth?.user.name }}
                                        </h3>
                                        <p class="mt-1 text-caption text-gray-500">
                                            {{ $page.props.auth?.user.email }}
                                        </p>
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-1 mt-7">
                                    <div v-for="item in userMenu" :key="item.to">
                                        <Link :href="route(item.to)" :class="isNavLinkActive(item)
                                            ? ' bg-secondary/10 text-primary rounded-lg'
                                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg'
                                            " class="flex items-center gap-4 px-3 py-2 transition-colors">
                                            <div>
                                                <v-icon v-if="item.icon" size="20" :icon="item.icon"
                                                    :class="isNavLinkActive(item) ? 'text-primary' : 'text-gray-500'"></v-icon>
                                            </div>
                                            <div class="text-sm font-medium">{{ item.title }}</div>
                                        </Link>
                                    </div>

                                    <div class="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <button @click.prevent="logout"
                                            class="w-full text-left px-3 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-sm font-medium transition-colors">
                                            {{ __t("logout") }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </v-menu>
                </div>

                <div v-else class="flex items-center gap-4">
                    <!-- <button @click="loginDialog = true"
                        :class=" !isScrolled
                            ? 'text-white hover:text-secondary font-medium transition-colors'
                            : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary font-medium transition-colors'">
                        {{ __t("login") }}
                    </button> -->
                    <Link :href="route('contact')"
                        :class="!isScrolled
                            ? 'bg-primary hover:scale-105 backdrop-blur-sm border border-white/40 text-white px-6 py-2.5 rounded-lg font-bold transition-all shadow-lg'
                            : 'bg-primary hover:scale-105 text-white px-6 py-2.5 rounded-lg font-bold transition-all shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30'">
                        {{ __t("contact_us") }}
                    </Link>
                </div>
            </div>
            <div class="flex items-center gap-4">
                <Link :href="route('contact')" class="lg:hidden"
                    :class="!isScrolled
                        ? 'bg-primary hover:scale-105 backdrop-blur-sm border border-white/40 text-white px-6 py-2.5 rounded-lg font-bold transition-all shadow-lg'
                        : 'bg-primary hover:scale-105 text-white px-6 py-2.5 rounded-lg font-bold transition-all shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30'">
                    {{ __t("contact_us") }}
                </Link>
                <!-- Mobile Menu Button -->
                <button @click="drawer = true" :class="!isScrolled
                    ? 'lg:hidden dark:text-white hover:text-secondary transition-colors'
                    : 'lg:hidden text-gray-600 dark:text-gray-300 hover:text-primary transition-colors'">
                    <span class="sr-only">Open Menu</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-8 h-8">
                        <path stroke-linecap="round" stroke-linejoin="round"
                            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                </button>
            </div>

        </div>
    </header>

    <!-- Dialogs -->
    <v-dialog v-model="loginDialog" width="800">
        <Login :fromDialog="true" :can-reset-password="true" @close="closeDialog" />
    </v-dialog>
    <v-dialog v-model="registerDialog" width="800" scrollable>
        <Register :fromDialog="true" @close="closeDialog" />
    </v-dialog>

    <!-- Mobile Drawer -->
    <v-no-ssr>
        <v-navigation-drawer v-model="drawer" location="right" width="320" temporary class="!fixed">
            <div class="flex flex-col h-full bg-white dark:bg-[#1e1e1e]">
                <!-- Drawer Header -->
                <div class="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <span class="font-bold text-lg text-primary">{{ __t("menu") }}</span>
                    <button @click="drawer = false" class="text-gray-400 hover:text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Content -->
                <div class="flex-1 overflow-y-auto p-4">
                    <!-- Settings in Drawer -->
                    <div class="flex items-center justify-end gap-2 mb-4">
                        <ThemeToggleBtn />
                        <LanguageSelectorVue />
                        <!-- <CurrencySelector /> -->
                    </div>
                    <!-- User Info in Drawer -->
                    <div class="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl" v-if="$page.props.auth?.user">
                        <div class="flex items-center gap-3 mb-4">
                            <img class="rounded-full h-12 w-12" :src="$page.props.auth?.user.profile_photo_url"
                                :alt="$page.props.auth?.user.name" />
                            <div>
                                <h3 class="font-bold text-sm">{{ $page.props.auth?.user.name }}</h3>
                                <p class="text-xs text-gray-500">{{ $page.props.auth?.user.email }}</p>
                            </div>
                        </div>

                        <div class="flex flex-col space-y-1">
                            <div v-for="item in userMenu" :key="item.to">
                                <Link :href="route(item.to)"
                                    class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors text-sm"
                                    :class="isNavLinkActive(item) ? 'text-primary font-medium' : 'text-gray-600 dark:text-gray-300'">
                                    <v-icon v-if="item.icon" size="18" :icon="item.icon"></v-icon>
                                    <span>{{ item.title }}</span>
                                </Link>
                            </div>
                        </div>

                        <button @click.prevent="logout"
                            class="w-full mt-4 flex items-center gap-2 px-3 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-sm font-medium transition-colors">
                            <v-icon icon="mdi-logout" size="18"></v-icon>
                            {{ __t("logout") }}
                        </button>
                    </div>

                    <!-- Mobile Nav Items -->
                    <div class="mb-6">
                        <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">{{
                            __t("quikLinks")
                        }}</div>
                        <MobileSilderNav :id="$page.props.setting?.menuMobileSlider" />
                    </div>

                    <!-- Actions in Drawer (if not logged in) -->
                    <div v-if="!$page.props.auth?.user" class="space-y-3">
                        <button @click="loginDialog = true"
                            class="w-full py-2.5 border border-primary text-primary rounded-lg font-bold hover:bg-primary/5 transition-colors">
                            {{ __t("login") }}
                        </button>
                    </div>
                </div>
            </div>
        </v-navigation-drawer>
    </v-no-ssr>
</template>
